// Illustrations for the technical analysis articles. They're drawn examples, not
// real market data: candles built from a few turning points, plus the lines and
// labels each article explains. Labels come in English and Spanish.

const W = 640;
const H = 300;
const PAD = { top: 24, right: 20, bottom: 28, left: 20 };

const LABELS = {
  en: {
    hh: 'Higher high', hl: 'Higher low', trend: 'Uptrend', range: 'Range', top: 'Range top', bottom: 'Range bottom',
    pdh: 'Prior day high', pdl: 'Prior day low', onh: 'Overnight high', round: 'Round number', reject: 'Rejected', bounce: 'Bounce',
    vwap: 'VWAP', pullback: 'Pullback to VWAP', above: 'Above VWAP: buyers in control',
    or: 'Opening range (first 15 min)', breakout: 'Breakout', retest: 'Retest', stop: 'Stop back inside the range',
    poc: 'Point of control', va: 'Value area (70% of volume)', volume: 'Volume at each price',
    today: 'Today: both agree on $5.00', harvest: 'At harvest', locked: 'Locked price: $5.00',
    upCase: 'Price rises to $6: baker still pays $5', downCase: 'Price falls to $4: farmer still gets $5',
    hours: ['6 PM', '9 PM', '12 AM', '3 AM', '6 AM', '9:30 AM', '12 PM', '4 PM'],
    asia: 'Overnight (Asia)', europe: 'Europe', us: 'US regular hours', breakLabel: 'Daily break', breakFull: 'Daily break 5–6 PM ↓', et: 'New York time (ET)',
    you: 'You', platform: 'Platform', platformSub: 'TopstepX, Tradovate, NinjaTrader', broker: 'Broker (FCM)', brokerSub: 'holds the account',
    exchange: 'Exchange', exchangeSub: 'CME Globex matches orders', clearing: 'Clearinghouse', clearingSub: 'guarantees every trade',
    regulator: 'Regulators: CFTC and NFA', fees: 'Fees on each side: commission · exchange · clearing · NFA', notScale: 'Not to scale',
    styles: [['Scalping', 'Seconds to minutes'], ['Day trading', 'Minutes to hours, flat by the close'], ['Swing trading', 'Days to weeks'], ['Position trading', 'Weeks to months']],
    moreTrades: '← More trades, more screen time', overnight: 'Fewer trades, overnight risk →',
    rr: 'Reward-to-risk (how much you make on a win for every $1 you risk)', winNeeded: 'Win rate needed to break even',
    profitZone: 'Above the line: profitable', lossZone: 'Below: losing money', after: 'Account left after 10 losses in a row', lossesX: 'Losses in a row',
    risk1: '1% per trade', risk2: '2% per trade', risk5: '5% per trade',
    timeline: [['1730', 'Rice futures in Osaka'], ['1848', 'Chicago Board of Trade'], ['1898', 'Butter and Egg Board (CME)'], ['1972', 'Currency futures'],
      ['1982', 'S&P 500 futures'], ['1992', 'Globex, electronic'], ['1997', 'E-mini S&P 500'], ['2012', 'Topstep founded'], ['2015', 'Most pits close'], ['2019', 'Micro E-minis']],
  },
  es: {
    hh: 'Máximo más alto', hl: 'Mínimo más alto', trend: 'Tendencia alcista', range: 'Rango', top: 'Techo del rango', bottom: 'Piso del rango',
    pdh: 'Máximo del día anterior', pdl: 'Mínimo del día anterior', onh: 'Máximo de la noche', round: 'Número redondo', reject: 'Rechazo', bounce: 'Rebote',
    vwap: 'VWAP', pullback: 'Retroceso al VWAP', above: 'Sobre el VWAP: mandan los compradores',
    or: 'Rango de apertura (primeros 15 min)', breakout: 'Ruptura', retest: 'Nueva prueba', stop: 'Stop dentro del rango',
    poc: 'Punto de control', va: 'Área de valor (70% del volumen)', volume: 'Volumen en cada precio',
    today: 'Hoy: ambos acuerdan $5.00', harvest: 'En la cosecha', locked: 'Precio fijado: $5.00',
    upCase: 'Sube a $6: el panadero sigue pagando $5', downCase: 'Baja a $4: el agricultor sigue cobrando $5',
    hours: ['6 p. m.', '9 p. m.', '12 a. m.', '3 a. m.', '6 a. m.', '9:30 a. m.', '12 p. m.', '4 p. m.'],
    asia: 'Noche (Asia)', europe: 'Europa', us: 'Horario regular de EE. UU.', breakLabel: 'Pausa diaria', breakFull: 'Pausa diaria de 5 a 6 p. m. ↓', et: 'Hora de Nueva York (ET)',
    you: 'Tú', platform: 'Plataforma', platformSub: 'TopstepX, Tradovate, NinjaTrader', broker: 'Bróker (FCM)', brokerSub: 'guarda la cuenta',
    exchange: 'Bolsa', exchangeSub: 'CME Globex une las órdenes', clearing: 'Cámara de compensación', clearingSub: 'garantiza cada operación',
    regulator: 'Reguladores: CFTC y NFA', fees: 'Comisiones por lado: bróker · bolsa · compensación · NFA', notScale: 'Sin escala',
    styles: [['Scalping', 'Segundos a minutos'], ['Day trading', 'Minutos a horas, cerrado al final del día'], ['Swing trading', 'Días a semanas'], ['Trading de posición', 'Semanas a meses']],
    moreTrades: '← Más operaciones, más tiempo frente a la pantalla', overnight: 'Menos operaciones, riesgo de noche →',
    rr: 'Relación ganancia-riesgo (cuánto ganas en una ganadora por cada $1 que arriesgas)', winNeeded: 'Porcentaje de acierto para no perder',
    profitZone: 'Sobre la línea: rentable', lossZone: 'Debajo: pierdes dinero', after: 'Lo que queda de la cuenta tras 10 pérdidas seguidas', lossesX: 'Pérdidas seguidas',
    risk1: '1% por operación', risk2: '2% por operación', risk5: '5% por operación',
    timeline: [['1730', 'Futuros de arroz en Osaka'], ['1848', 'Chicago Board of Trade'], ['1898', 'Butter and Egg Board (CME)'], ['1972', 'Futuros de divisas'],
      ['1982', 'Futuros del S&P 500'], ['1992', 'Globex, electrónico'], ['1997', 'E-mini S&P 500'], ['2012', 'Nace Topstep'], ['2015', 'Cierran casi todos los pits'], ['2019', 'Micro E-mini']],
  },
};

// Candles from turning points: each leg moves from one point to the next over `per` candles.
function candles(points, per = 4) {
  const list = [];
  let prev = points[0];
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1];
    const to = points[i];
    for (let k = 1; k <= per; k++) {
      const close = from + ((to - from) * k) / per + Math.sin(list.length * 1.7) * 0.6;
      const open = prev;
      const wick = 0.8 + Math.abs(Math.cos(list.length * 2.3)) * 1.2;
      list.push({ open, close, high: Math.max(open, close) + wick, low: Math.min(open, close) - wick });
      prev = close;
    }
  }
  return list;
}

function scale(data, lo, hi, width = W) {
  const n = data.length;
  const step = (width - PAD.left - PAD.right) / n;
  const x = (i) => PAD.left + step * i + step / 2;
  const y = (p) => PAD.top + ((hi - p) / (hi - lo)) * (H - PAD.top - PAD.bottom);
  return { x, y, body: Math.max(3, step * 0.55) };
}

function Candles({ data, s }) {
  return data.map((c, i) => {
    const up = c.close >= c.open;
    const top = s.y(Math.max(c.open, c.close));
    const height = Math.max(1.5, Math.abs(s.y(c.open) - s.y(c.close)));
    return (
      // eslint-disable-next-line react/no-array-index-key
      <g key={i} className={up ? 'dg-up' : 'dg-down'}>
        <line x1={s.x(i)} x2={s.x(i)} y1={s.y(c.high)} y2={s.y(c.low)} className="dg-wick" />
        <rect x={s.x(i) - s.body / 2} y={top} width={s.body} height={height} rx="1" />
      </g>
    );
  });
}

function Level({ s, price, label, className = '', x1 = PAD.left, x2 = W - PAD.right, below = false }) {
  return (
    <g className={`dg-level ${className}`}>
      <line x1={x1} x2={x2} y1={s.y(price)} y2={s.y(price)} />
      {label && <text x={x2 - 4} y={s.y(price) + (below ? 14 : -6)} textAnchor="end">{label}</text>}
    </g>
  );
}

function Note({ x, y, text, anchor = 'middle', className = '' }) {
  return <text x={x} y={y} textAnchor={anchor} className={`dg-note ${className}`}>{text}</text>;
}

function Structure({ L }) {
  const trend = candles([100, 108, 104, 113, 109, 119, 114, 124], 3);
  const range = candles([104, 113, 104.5, 112.5, 104, 113, 105, 112], 3);
  const half = W / 2;
  const s1 = scale(trend, 96, 128, half);
  const s2raw = scale(range, 96, 128, half);
  const s2 = { ...s2raw, x: (i) => s2raw.x(i) + half };
  return (
    <>
      <Candles data={trend} s={s1} />
      <Note x={s1.x(8)} y={PAD.top - 6} text={L.trend} className="dg-title" />
      <Note x={s1.x(5)} y={s1.y(trend[5].low) + 16} text={L.hl} />
      <Note x={s1.x(11)} y={s1.y(trend[11].low) + 16} text={L.hl} />
      <Note x={s1.x(14)} y={s1.y(trend[14].high) - 8} text={L.hh} />
      <Note x={s1.x(17)} y={s1.y(trend[17].low) + 16} text={L.hl} />
      <Note x={s1.x(20)} y={s1.y(trend[20].high) - 8} text={L.hh} />
      <line x1={half} x2={half} y1={PAD.top} y2={H - PAD.bottom} className="dg-divider" />
      <Candles data={range} s={s2} />
      <Level s={s2} price={113.6} label={L.top} x1={half + 10} />
      <Level s={s2} price={103.4} label={L.bottom} x1={half + 10} below />
      <Note x={half + half / 2} y={PAD.top - 6} text={L.range} className="dg-title" />
    </>
  );
}

function Levels({ L }) {
  const data = candles([105, 109, 107, 114, 110, 103, 99, 103, 101, 108, 106, 111], 3);
  const s = scale(data, 94, 124);
  return (
    <>
      <Level s={s} price={120} label={L.pdh} className="dg-key" />
      <Level s={s} price={114.6} label={L.onh} />
      <Level s={s} price={110} label={L.round} className="dg-soft" />
      <Level s={s} price={98.6} label={L.pdl} className="dg-key" below />
      <Candles data={data} s={s} />
      <Note x={s.x(9)} y={s.y(116.5)} text={`${L.reject} ↓`} />
      <Note x={s.x(18)} y={s.y(95.5)} text={`${L.bounce} ↑`} />
    </>
  );
}

function vwapLine(data) {
  let sum = 0;
  return data.map((c, i) => {
    sum += (c.high + c.low + c.close) / 3;
    return sum / (i + 1);
  });
}

function Vwap({ L }) {
  const data = candles([100, 106, 103.5, 110, 107.5, 115, 112, 119], 3);
  const s = scale(data, 96, 122);
  const v = vwapLine(data);
  const path = v.map((p, i) => `${i ? 'L' : 'M'}${s.x(i)},${s.y(p)}`).join(' ');
  return (
    <>
      <path d={path} className="dg-vwap" />
      <Candles data={data} s={s} />
      <Note x={s.x(data.length - 1) + 6} y={s.y(v[v.length - 1]) + 18} text={L.vwap} anchor="end" className="dg-vwap-label" />
      <Note x={s.x(12)} y={s.y(103)} text={`↑ ${L.pullback}`} />
      <Note x={s.x(4)} y={PAD.top + 8} text={L.above} anchor="start" />
    </>
  );
}

function Orb({ L }) {
  const data = [
    { open: 106, close: 109, high: 110, low: 105 },
    { open: 109, close: 105.5, high: 109.6, low: 104 },
    { open: 105.5, close: 108, high: 108.8, low: 104.6 },
    ...candles([108, 113, 110.4, 117, 115, 121], 3).map((c, i) => (i === 0 ? { ...c, open: 108 } : c)),
  ];
  const s = scale(data, 100, 124);
  const boxX = s.x(0) - s.body;
  const boxW = s.x(2) - s.x(0) + s.body * 2;
  return (
    <>
      <rect x={boxX} y={s.y(110)} width={boxW} height={s.y(104) - s.y(110)} className="dg-box" />
      <Level s={s} price={110} x1={boxX} className="dg-soft" />
      <Level s={s} price={106.5} label={L.stop} x1={boxX + boxW} className="dg-stop" below />
      <Candles data={data} s={s} />
      <Note x={boxX} y={s.y(104) + 16} text={L.or} anchor="start" />
      <Note x={s.x(5)} y={s.y(114.5)} text={`${L.breakout} ↑`} />
      <Note x={s.x(8)} y={s.y(108.6)} text={`↑ ${L.retest}`} />
    </>
  );
}

function Profile({ L }) {
  const data = candles([106, 112, 107, 110, 105, 109, 108, 113, 107, 110], 3);
  const chartW = W * 0.68;
  const s = scale(data, 101, 117, chartW);
  // Volume at each price: more where price spent more time, as a profile does.
  const buckets = [];
  for (let p = 102; p <= 116; p += 1) {
    const touches = data.filter((c) => c.low <= p && c.high >= p).length;
    buckets.push({ p, v: touches + Math.max(0, 3 - Math.abs(p - 108.5)) * 1.5 });
  }
  const max = Math.max(...buckets.map((b) => b.v));
  const poc = buckets.find((b) => b.v === max);
  const total = buckets.reduce((sum, b) => sum + b.v, 0);
  // Value area: grow outward from the point of control until 70% of volume is inside.
  let lo = buckets.indexOf(poc);
  let hi = lo;
  let inside = poc.v;
  while (inside < total * 0.7) {
    const below = lo > 0 ? buckets[lo - 1].v : -1;
    const above = hi < buckets.length - 1 ? buckets[hi + 1].v : -1;
    if (above >= below) { hi += 1; inside += above; } else { lo -= 1; inside += below; }
  }
  const barX = chartW + 10;
  const barMax = W - PAD.right - barX;
  const rowH = (s.y(102) - s.y(116)) / buckets.length;
  return (
    <>
      <rect x={PAD.left} y={s.y(buckets[hi].p + 0.5)} width={W - PAD.left - PAD.right} height={s.y(buckets[lo].p - 0.5) - s.y(buckets[hi].p + 0.5)} className="dg-va" />
      <Candles data={data} s={s} />
      {buckets.map((b, i) => (
        <rect key={b.p} x={barX} y={s.y(b.p) - rowH / 2 + 1} width={(b.v / max) * barMax} height={Math.max(2, rowH - 2)}
          className={i >= lo && i <= hi ? (b === poc ? 'dg-bar dg-poc-bar' : 'dg-bar dg-in') : 'dg-bar'} />
      ))}
      <Level s={s} price={poc.p} label={L.poc} className="dg-poc" />
      <Note x={PAD.left + 4} y={s.y(buckets[hi].p + 0.5) - 6} text={L.va} anchor="start" />
      <Note x={barX} y={PAD.top - 8} text={L.volume} anchor="start" />
    </>
  );
}

// A farmer and a baker lock in $5.00 today; whatever the price does by harvest, they trade at $5.
function Hedge({ L }) {
  const x0 = 200;
  const x1 = 560;
  const y = (p) => 40 + (6.5 - p) * 70;
  return (
    <>
      <Level s={{ y }} price={5} label={L.locked} className="dg-key" x1={x0} x2={x1 + 60} below />
      <path d={`M${x0},${y(5)} C220,${y(5.2)} 380,${y(5.7)} ${x1},${y(6)}`} className="dg-path dg-path-up" />
      <path d={`M${x0},${y(5)} C220,${y(4.9)} 380,${y(4.3)} ${x1},${y(4)}`} className="dg-path dg-path-down" />
      <circle cx={x0} cy={y(5)} r="6" className="dg-dot" />
      <circle cx={x1} cy={y(6)} r="6" className="dg-dot dg-dot-up" />
      <circle cx={x1} cy={y(4)} r="6" className="dg-dot dg-dot-down" />
      <Note x={x0 - 14} y={y(5) + 4} text={L.today} anchor="end" />
      <Note x={x1} y={y(6) - 14} text={L.upCase} anchor="end" />
      <Note x={x1} y={y(4) + 24} text={L.downCase} anchor="end" />
      <Note x={x1} y={H - 8} text={L.harvest} anchor="middle" className="dg-title" />
    </>
  );
}

function Timeline({ L }) {
  const left = 40;
  const right = W - 40;
  const mid = 150;
  const step = (right - left) / (L.timeline.length - 1);
  return (
    <>
      <line x1={left} x2={right} y1={mid} y2={mid} className="dg-axis" />
      {L.timeline.map(([year, text], i) => {
        const x = left + step * i;
        const up = i % 2 === 0;
        return (
          <g key={year}>
            <line x1={x} x2={x} y1={mid} y2={up ? mid - 40 : mid + 40} className="dg-tick" />
            <circle cx={x} cy={mid} r="5" className={i === 7 ? 'dg-dot dg-dot-up' : 'dg-dot'} />
            <text x={x} y={up ? mid - 86 : mid + 60} textAnchor="middle" className="dg-year">{year}</text>
            <WrapNote x={x} y={up ? mid - 48 : mid + 76} text={text} up={up} />
          </g>
        );
      })}
      <Note x={right} y={H - 8} text={L.notScale} anchor="end" className="dg-soft-note" />
    </>
  );
}

// Splits a short label over two lines so neighbours don't collide.
function WrapNote({ x, y, text, up, title }) {
  const words = text.split(' ');
  const half = Math.ceil(words.length / 2);
  const lines = words.length > 2 || (title && words.length > 1) ? [words.slice(0, half).join(' '), words.slice(half).join(' ')] : [text];
  const start = up ? y - (lines.length - 1) * 13 : y;
  return (
    <text x={x} y={start} textAnchor="middle" className={title ? 'dg-node-title' : 'dg-note dg-small'}>
      {lines.map((line, i) => <tspan key={line} x={x} dy={i ? (title ? 16 : 13) : 0}>{line}</tspan>)}
    </text>
  );
}

// One futures trading day in New York time: 6 PM to 5 PM the next day, then a one-hour break.
function Hours({ L }) {
  const left = 30;
  const right = W - 30;
  const span = 24; // hours drawn, 6 PM to 6 PM
  const x = (h) => left + ((right - left) * h) / span;
  const top = 110;
  const height = 60;
  const blocks = [
    { from: 0, to: 9, label: L.asia, cls: 'dg-seg' },
    { from: 9, to: 15.5, label: L.europe, cls: 'dg-seg dg-seg-2' },
    { from: 15.5, to: 22, label: L.us, cls: 'dg-seg dg-seg-us' },
    { from: 22, to: 23, label: '', cls: 'dg-seg' },
    { from: 23, to: 24, label: L.breakLabel, cls: 'dg-seg dg-seg-break' },
  ];
  const ticks = [0, 3, 6, 9, 12, 15.5, 18, 22];
  return (
    <>
      {blocks.map((b) => (
        <g key={b.from}>
          <rect x={x(b.from)} y={top} width={x(b.to) - x(b.from) - 2} height={height} rx="4" className={b.cls} />
          {b.label && b.from !== 23 && <text x={(x(b.from) + x(b.to)) / 2} y={top + height / 2 + 5} textAnchor="middle" className="dg-seg-label">{b.label}</text>}
        </g>
      ))}
      <Note x={x(24)} y={top - 12} text={L.breakFull} anchor="end" className="dg-break-note" />
      {ticks.map((h, i) => (
        <g key={h}>
          <line x1={x(h)} x2={x(h)} y1={top + height + 4} y2={top + height + 12} className="dg-tick" />
          <text x={x(h)} y={top + height + 28} textAnchor={i === 0 ? 'start' : 'middle'} className="dg-hour">{L.hours[i]}</text>
        </g>
      ))}
      <Note x={left} y={top + height + 64} text={L.et} anchor="start" className="dg-soft-note" />
    </>
  );
}

// Who sits between a trader's click and the exchange.
function Flow({ L }) {
  const boxes = [
    { x: 12, title: L.you, sub: '' },
    { x: 132, title: L.platform, sub: L.platformSub },
    { x: 252, title: L.broker, sub: L.brokerSub },
    { x: 372, title: L.exchange, sub: L.exchangeSub },
  ];
  const w = 104;
  const y = 70;
  const h = 78;
  return (
    <>
      {boxes.map((b, i) => (
        <g key={b.title}>
          <rect x={b.x} y={y} width={w} height={h} rx="8" className={i === 3 ? 'dg-node dg-node-key' : 'dg-node'} />
          <text x={b.x + w / 2} y={y + 32} textAnchor="middle" className="dg-node-title">{b.title}</text>
          {b.sub && <WrapNote x={b.x + w / 2} y={y + 52} text={b.sub} />}
          {i < boxes.length - 1 && <path d={`M${b.x + w + 2},${y + h / 2} H${boxes[i + 1].x - 4}`} className="dg-arrow" markerEnd="url(#dg-arrowhead)" />}
        </g>
      ))}
      <rect x={516} y={y} width={112} height={h} rx="8" className="dg-node" />
      <WrapNote x={572} y={y + 32} text={L.clearing} title />
      <path d={`M${372 + w + 4},${y + h / 2} H${512}`} className="dg-arrow" markerEnd="url(#dg-arrowhead)" markerStart="url(#dg-arrowhead-start)" />
      <WrapNote x={572} y={y + h + 20} text={L.clearingSub} />
      <rect x={150} y={200} width={475} height={34} rx="6" className="dg-reg" />
      <text x={387} y={222} textAnchor="middle" className="dg-note">{L.regulator}</text>
      <Note x={20} y={268} text={L.fees} anchor="start" className="dg-soft-note" />
      <defs>
        <marker id="dg-arrowhead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L10,5 L0,10 z" className="dg-arrowhead" /></marker>
        <marker id="dg-arrowhead-start" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M10,0 L0,5 L10,10 z" className="dg-arrowhead" /></marker>
      </defs>
    </>
  );
}

// Trading styles along a line of how long each trade is held.
function Styles({ L }) {
  const left = 24;
  const gap = 8;
  const w = (W - left * 2 - gap * 3) / 4;
  return (
    <>
      {L.styles.map(([name, time], i) => (
        <g key={name}>
          <rect x={left + i * (w + gap)} y={80} width={w} height={96} rx="8" className={`dg-node${i === 1 ? ' dg-node-key' : ''}`} />
          <text x={left + i * (w + gap) + w / 2} y={118} textAnchor="middle" className="dg-node-title">{name}</text>
          <WrapNote x={left + i * (w + gap) + w / 2} y={142} text={time} />
        </g>
      ))}
      <Note x={left} y={214} text={L.moreTrades} anchor="start" />
      <Note x={W - left} y={214} text={L.overnight} anchor="end" />
    </>
  );
}

// The win rate needed to break even at each reward-to-risk: 1 / (1 + R).
function Expectancy({ L }) {
  const box = { left: 64, right: W - 24, top: 24, bottom: 236 };
  const x = (r) => box.left + ((r - 0.5) / 2.5) * (box.right - box.left);
  const y = (pct) => box.bottom - (pct / 80) * (box.bottom - box.top);
  const points = [];
  for (let r = 0.5; r <= 3.001; r += 0.05) points.push([r, 100 / (1 + r)]);
  const line = points.map(([r, p], i) => `${i ? 'L' : 'M'}${x(r)},${y(p)}`).join(' ');
  const area = `${line} L${x(3)},${box.top} L${x(0.5)},${box.top} Z`;
  return (
    <>
      <path d={area} className="dg-va" />
      {[20, 40, 60, 80].map((p) => (
        <g key={p}>
          <line x1={box.left} x2={box.right} y1={y(p)} y2={y(p)} className="dg-divider" />
          <text x={box.left - 8} y={y(p) + 4} textAnchor="end" className="dg-hour">{p}%</text>
        </g>
      ))}
      {[0.5, 1, 1.5, 2, 2.5, 3].map((r) => <text key={r} x={x(r)} y={box.bottom + 18} textAnchor="middle" className="dg-hour">{r}:1</text>)}
      <path d={line} className="dg-path dg-path-up" />
      {[[0.5, 66.7], [1, 50], [2, 33.3], [3, 25]].map(([r, p]) => (
        <g key={r}>
          <circle cx={x(r)} cy={y(p)} r="5" className="dg-dot" />
          <text x={x(r) + (r === 3 ? -8 : 8)} y={y(p) - 9} textAnchor={r === 3 ? 'end' : 'start'} className="dg-note">{Math.round(p)}%</text>
        </g>
      ))}
      <Note x={x(2.2)} y={y(62)} text={L.profitZone} />
      <Note x={x(1.1)} y={y(22)} text={L.lossZone} className="dg-soft-note" />
      <Note x={(box.left + box.right) / 2} y={H - 4} text={L.rr} className="dg-soft-note" />
      <text x={14} y={box.top - 8} className="dg-soft-note">{L.winNeeded}</text>
    </>
  );
}

// How much of the account is left after a losing streak, at different risk per trade.
function Ruin({ L }) {
  const box = { left: 56, right: W - 120, top: 28, bottom: 240 };
  const x = (n) => box.left + (n / 10) * (box.right - box.left);
  const y = (pct) => box.bottom - ((pct - 50) / 50) * (box.bottom - box.top);
  const series = [[0.01, L.risk1, 'dg-path-up'], [0.02, L.risk2, 'dg-path-mid'], [0.05, L.risk5, 'dg-path-down']];
  return (
    <>
      {[50, 60, 70, 80, 90, 100].map((p) => (
        <g key={p}>
          <line x1={box.left} x2={box.right} y1={y(p)} y2={y(p)} className="dg-divider" />
          <text x={box.left - 8} y={y(p) + 4} textAnchor="end" className="dg-hour">{p}%</text>
        </g>
      ))}
      {[0, 2, 4, 6, 8, 10].map((n) => <text key={n} x={x(n)} y={box.bottom + 18} textAnchor="middle" className="dg-hour">{n}</text>)}
      {series.map(([risk, label, cls]) => {
        const pts = Array.from({ length: 11 }, (_, n) => [n, 100 * (1 - risk) ** n]);
        const end = pts[10][1];
        return (
          <g key={label}>
            <path d={pts.map(([n, p], i) => `${i ? 'L' : 'M'}${x(n)},${y(p)}`).join(' ')} className={`dg-path ${cls}`} />
            <circle cx={x(10)} cy={y(end)} r="4.5" className="dg-dot" />
            <text x={x(10) + 10} y={y(end) - 2} className="dg-note">{label}</text>
            <text x={x(10) + 10} y={y(end) + 13} className="dg-small">{Math.round(end)}%</text>
          </g>
        );
      })}
      <text x={14} y={box.top - 10} className="dg-soft-note">{L.after}</text>
      <Note x={(box.left + box.right) / 2} y={H - 4} text={L.lossesX} className="dg-soft-note" />
    </>
  );
}

const DIAGRAMS = { styles: Styles, expectancy: Expectancy, ruin: Ruin, structure: Structure, levels: Levels, vwap: Vwap, orb: Orb, profile: Profile, hedge: Hedge, timeline: Timeline, hours: Hours, flow: Flow };

export function Diagram({ name, caption, lang }) {
  const Draw = DIAGRAMS[name];
  if (!Draw) return null;
  const L = LABELS[lang] || LABELS.en;
  return (
    <figure className="blog-diagram">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={caption}>
        <Draw L={L} />
      </svg>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}
